import { useRequestURL } from '#imports'

import { toBcp47Locale } from '@backmarket/utils/string/toBcp47Locale'

import { useMarketplace } from './useMarketplace'

type MarketDomainTarget = 'main' | 'accounts' | 'payment'

type ValidSubDomain =
  | 'www'
  | 'preprod'
  | `${'accounts' | 'payment'}${'.preprod' | ''}`

function getSubDomain(
  baseTarget: MarketDomainTarget,
  isPreprod: boolean,
): ValidSubDomain {
  if (baseTarget === 'main' || baseTarget === 'payment') {
    return isPreprod ? 'preprod' : 'www'
  }

  return isPreprod ? `${baseTarget}.preprod` : baseTarget
}

/**
 * Based on the current environment, get the base URL of a given target.
 *
 * The targets are defined as follows:
 * - `main` is the main marketplace URL (www.backmarket.TLD or preprod.backmarket.TLD)
 * - `accounts` is the accounts URL (accounts.backmarket.TLD or accounts.preprod.backmarket.TLD)
 * - `payment` currently redirects to the main marketplace URL (reserved for future payment subdomain)
 * @example
 * ```ts
 * // current URL: https://www.backmarket.fr/fr-fr
 * const baseUrl = useMarketBaseUrl('accounts')
 * // baseUrl > 'https://accounts.backmarket.fr'
 * ```
 */
export function useMarketBaseUrl(
  baseTarget?: MarketDomainTarget,
  locale?: string,
) {
  const target = baseTarget ?? 'main'
  const { protocol, hostname } = useRequestURL()

  const isPreprod =
    hostname.includes('preprod.backmarket.') ||
    // To simplify the local development, local URL will be redirected to preprod
    hostname.includes('local.backmarket.')

  const { market } = useMarketplace()

  const subDomain = getSubDomain(target, isPreprod)

  const baseUrl = `${protocol}//${subDomain}${market.domain}`
  const baseUrlWithLocale = `${baseUrl}/${toBcp47Locale(locale ?? market.defaultLocale)}`

  return {
    /**
     * The full URL of the targeted domain. No trailing slash.
     * @example https://www.backmarket.com
     */
    baseUrl,

    /**
     * The full URL of the targeted domain with the locale prefix. No trailing slash.
     * @example https://www.backmarket.com/en-us
     */
    baseUrlWithLocale,
  }
}
